// src/utils/nfeUtils.js

/**
 * Valida o dígito verificador (Módulo 11) de uma Chave de Acesso de NF-e (44 dígitos).
 *
 * @param {string} chave 44 dígitos numéricos
 * @returns {boolean} true se o dígito verificador for matematicamente válido
 */
export const validarDigitoVerificadorNFe = (chave) => {
  if (!chave || typeof chave !== "string") return false;
  const limpa = chave.replace(/\D/g, "");
  if (limpa.length !== 44) return false;

  const base = limpa.substring(0, 43);
  const digitoInformado = parseInt(limpa.charAt(43), 10);

  let soma = 0;
  let peso = 2;

  for (let i = base.length - 1; i >= 0; i--) {
    soma += parseInt(base.charAt(i), 10) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }

  const resto = soma % 11;
  const digitoCalculado = resto === 0 || resto === 1 ? 0 : 11 - resto;

  return digitoCalculado === digitoInformado;
};

/**
 * Extrai a chave de acesso (44 dígitos) de uma leitura de scanner.
 * Suporta:
 * 1. Código de barras linear puro (Code 128 com 44 números)
 * 2. URL de QR Code de NFC-e (com parâmetro chNFe ou padrão de pipe `p=...`)
 *
 * @param {string} rawText Texto bruto lido pela câmera
 * @returns {string|null} Chave de 44 dígitos ou null se não encontrada
 */
export const extrairChaveAcesso = (rawText) => {
  if (!rawText || typeof rawText !== "string") return null;

  // 1. Caso direto: texto com 44 dígitos numéricos
  const apenasNumeros = rawText.replace(/\D/g, "");
  if (apenasNumeros.length === 44 && validarDigitoVerificadorNFe(apenasNumeros)) {
    return apenasNumeros;
  }

  // 2. Caso QR Code com query param 'chNFe=' ou 'chave='
  const paramMatch = rawText.match(/(?:chNFe|chave|p)=([0-9]{44})/i);
  if (paramMatch && paramMatch[1] && validarDigitoVerificadorNFe(paramMatch[1])) {
    return paramMatch[1];
  }

  // 3. Caso QR Code no padrão nacional com pipe: '...p=CHAVE|2|1|...'
  const pipeMatch = rawText.match(/p=([0-9]{44})\|/i);
  if (pipeMatch && pipeMatch[1] && validarDigitoVerificadorNFe(pipeMatch[1])) {
    return pipeMatch[1];
  }

  // 4. Procura por qualquer sequência contínua de 44 dígitos dentro da string
  const regexGenerica = /\b([0-9]{44})\b/g;
  const matches = rawText.match(regexGenerica);
  if (matches) {
    for (const match of matches) {
      if (validarDigitoVerificadorNFe(match)) {
        return match;
      }
    }
  }

  return null;
};

/**
 * Emite um bipe sonoro de confirmação usando a Web Audio API (sem dependência de arquivos de áudio).
 */
export const playBeepSucesso = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime); // Tom A5 (agradável e nítido)
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08); // Rampa suave até E6

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.12);
  } catch (e) {
    // Silencia se o navegador bloquear autoplay de áudio
  }
};

/**
 * Dispara vibração háptica no smartphone.
 */
export const vibrarSucesso = () => {
  if (typeof navigator !== "undefined" && navigator.vibrate) {
    try {
      navigator.vibrate([60, 40, 60]);
    } catch (e) {
      // Silencia se não suportado
    }
  }
};
